import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {MenuDateUtil} from '@emm/core/util/menu-date.util';
import {MenuKey, MenuKeyContext} from '../models/menu-key.model';
import {ApiUrl} from '@emm/core/util/api-url.token';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {ItemSummary} from '@emm/core/models/item-summary.model';
import {Submenu} from '@emm/core/models/submenu.model';
import {FeatureScope} from '@emm/core/util/feature-scope.constants';
import { environment } from '@env';


@Injectable({ providedIn: 'root' })
export class MenuService {

    static getDateParam(effectiveDate) {
        let getRequestParams = {};
        if (MenuDateUtil.isFutureDate(effectiveDate)) {
            effectiveDate = MenuDateUtil.formatDateToMidnight(effectiveDate);
            getRequestParams = {params: {effectiveDate}};
        }
        return getRequestParams;
    }

    static getParamsWithLocation(effectiveDate, menuKey, featureScope?) {
        let getRequestParams = {};
        if (MenuDateUtil.isFutureDate(effectiveDate) && featureScope !== 'Client') {
            effectiveDate = MenuDateUtil.formatDateToMidnight(effectiveDate);
            getRequestParams = {params: {effectiveDate, location: menuKey.location}};
        } else {
            getRequestParams = {params: {location: menuKey.location}};
        }
        return getRequestParams;
    }

    static getAllParams(effectiveDate, menuKey, language) {
        let getRequestParams = {};
        if (MenuDateUtil.isFutureDate(effectiveDate)) {
            effectiveDate = MenuDateUtil.formatDateToMidnight(effectiveDate);
            getRequestParams = {params: {effectiveDate, location: menuKey.location, language}};
        } else {
            getRequestParams = {params: {location: menuKey.location, language}};
        }
        return getRequestParams;
    }
    constructor(@Inject(ApiUrl) private apiUrl: string, private http: HttpClient) {}


    getMenuFilteredByItemTag(tag: string, consumer?: string, effectiveDate?: string) {
        // We aren't using them in the UI, but this API call also supports a location and a language parameter if they
        // are ever needed.

        if (effectiveDate) {
            return consumer ? this.http.get<any[]>(
                    `${this.getSubmenuBaseUrlV4()}/item/search?tag=${tag}&consumer=${consumer}&effectiveDate=${effectiveDate}`) :
                this.http.get<ItemDetail>(`${this.getSubmenuBaseUrlV4()}/item/search?tag=${tag}&effectiveDate=${effectiveDate}`);
        } else {
            return consumer ? this.http.get<any[]>(
                    `${this.getSubmenuBaseUrlV4()}/item/search?tag=${tag}&consumer=${consumer}`) :
                this.http.get<ItemDetail>(`${this.getSubmenuBaseUrlV4()}/item/search?tag=${tag}`);
        }
    }

    downloadMenu(endpoint: string): Observable<any> {
        return this.http.get(`${environment.menuGatewayUrl}/${endpoint}`, {
            headers: new HttpHeaders({Accept: 'application/octet-stream'}),
            responseType: 'blob'
        });
    }

    downloadItemsReport(endpoint: string): Observable<any> {
        return this.http.get(`${environment.apiUrl}/${endpoint}`, {
            headers: new HttpHeaders({Accept: '*/*'}),
            responseType: 'blob'
        });
    }

    getLastPublishedDateForMenu(endpoint: string): Observable<any> {
        return this.http.get(`${environment.menuGatewayUrl}/${endpoint}`, {});
    }

    updateMenuItemV4(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        path: string | object,
        updates: any,
        cascade: boolean,
        submenus: string[],
        forceOverrideInheritance: boolean
    ): Observable<ItemDetail> {
        let url = `${this.getPatchBaseUrlV4()}/update`;
        const requestParams = MenuService.getDateParam(effectiveDate);
        const paramsWithLocation = MenuService.getParamsWithLocation(effectiveDate, menuKey);
        if (cascade) {
            url = url + '/cascade';
            if (menuKey.location === 'CHAIN' && path['ancestorMatchPath']) {
                return this.http.patch<ItemDetail>(url,
                    {
                        subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey.submenu}`],
                        itemMatchPath: path['itemMatchPath'],
                        ancestorMatchPath: path['ancestorMatchPath'],
                        overrides: updates,
                        forceOverrideInheritance
                    },
                    requestParams);
            } else if (menuKey.location === 'CHAIN') {
                return this.http.patch<ItemDetail>(url,
                    {
                        subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey.submenu}`],
                        itemMatchPath: path['itemMatchPath'],
                        overrides: updates,
                        forceOverrideInheritance
                    },
                    requestParams);
            } else if (menuKey.location !== 'CHAIN' && path['ancestorMatchPath']) {
                return this.http.patch<ItemDetail>(url,
                    {
                        subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey.submenu}`],
                        itemMatchPath: path['itemMatchPath'],
                        ancestorMatchPath: path['ancestorMatchPath'],
                        overrides: updates,
                        forceOverrideInheritance
                    },
                    paramsWithLocation);
            } else {
                return this.http.patch<ItemDetail>(url,
                    {
                        subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey.submenu}`],
                        itemMatchPath: path['itemMatchPath'],
                        overrides: updates,
                        forceOverrideInheritance
                    },
                    paramsWithLocation);
            }
        } else {
            if (menuKey.location === 'CHAIN') {
                return this.http.patch<ItemDetail>(url, {itemPath: path, overrides: updates,
                    forceOverrideInheritance}, requestParams);
            } else {
                return this.http.patch<ItemDetail>(url, {itemPath: path, overrides: updates,
                    forceOverrideInheritance}, paramsWithLocation);
            }
        }
    }

    updateMenuItemV4New(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        path: string | object,
        updates: any,
        cascade: boolean,
        submenus: string[],
        forceOverrideInheritanceMap: any
    ): Observable<ItemDetail> {
        let url = `${this.getPatchBaseUrlV4()}/update`;
        const requestParams = MenuService.getDateParam(effectiveDate);
        const paramsWithLocation = MenuService.getParamsWithLocation(effectiveDate, menuKey);
        if (cascade) {
            url = url + '/cascade';
            if (menuKey.location === 'CHAIN' && path['ancestorMatchPath']) {
                return this.http.patch<ItemDetail>(url,
                    {
                        subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey.submenu}`],
                        itemMatchPath: path['itemMatchPath'],
                        ancestorMatchPath: path['ancestorMatchPath'],
                        overrides: updates,
                        forceOverrideInheritanceMap
                    },
                    requestParams);
            } else if (menuKey.location === 'CHAIN') {
                return this.http.patch<ItemDetail>(url,
                    {
                        subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey.submenu}`],
                        itemMatchPath: path['itemMatchPath'],
                        overrides: updates,
                        forceOverrideInheritanceMap
                    },
                    requestParams);
            } else if (menuKey.location !== 'CHAIN' && path['ancestorMatchPath']) {
                return this.http.patch<ItemDetail>(url,
                    {
                        subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey.submenu}`],
                        itemMatchPath: path['itemMatchPath'],
                        ancestorMatchPath: path['ancestorMatchPath'],
                        overrides: updates,
                        forceOverrideInheritanceMap
                    },
                    paramsWithLocation);
            } else {
                return this.http.patch<ItemDetail>(url,
                    {
                        subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey.submenu}`],
                        itemMatchPath: path['itemMatchPath'],
                        overrides: updates,
                        forceOverrideInheritanceMap
                    },
                    paramsWithLocation);
            }
        } else {
            if (menuKey.location === 'CHAIN') {
                return this.http.patch<ItemDetail>(url, {itemPath: path, overrides: updates,
                    forceOverrideInheritanceMap}, requestParams);
            } else {
                return this.http.patch<ItemDetail>(url, {itemPath: path, overrides: updates,
                    forceOverrideInheritanceMap}, paramsWithLocation);
            }
        }
    }

    moveMenuItemV4(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        from: string,
        to: string,
        index: string
    ): Observable<any> {
        const requestParams = MenuService.getDateParam(effectiveDate);
        return this.http.patch(
            `${this.getPatchBaseUrlV4()}/move`,
                {
                    itemPath: from,
                    targetParent: to,
                    targetIndex: index
                },
            requestParams
        );
    }


    addMenuItemsV4(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        parent: ItemDetail,
        children: ItemDetail[],
        isLegacy: boolean
    ): Observable<any> {
        const dateParams = MenuService.getDateParam(effectiveDate);
        const params = { ...(dateParams as any)?.params, isLegacy };
        return this.http.patch(`${this.getPatchBaseUrlV4()}/add`,
            {parentPath: parent.tagPath, item: children[0].tag}, {params});
    }


    addSubmenuV4(
        menuKey: MenuKeyContext,
        submenu: any,
    ): Observable<any> {
        const url = `${this.getGlobalSubmenuBaseUrlV4()}`;
        return this.http.post(url, {
            id: submenu.id,
            name: submenu.name,
            copyFromId: submenu.copyFromId});
    }


    addMenuItemCascadeV4(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        submenus: string[],
        parentTag: string,
        childTag: string
    ): Observable<any> {
        const requestParams = MenuService.getDateParam(effectiveDate);
        return this.http.patch(`${this.getPatchBaseUrlV4()}/add/cascade`,
            {subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey.submenu}`], parentMatchPath: `/${parentTag}`,
                item: childTag}, requestParams);
    }


    pasteMenuItemCascadeV4(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        submenus: string[],
        parentTag: string,
        childTagPath: string,
        position: string
    ): Observable<any> {
        const requestParams = MenuService.getDateParam(effectiveDate);
        return this.http.patch(`${this.getPatchBaseUrlV4()}/paste/cascade`,
            {subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey.submenu}`], parentMatchPath: `/${parentTag}`,
                sourceItemPath: childTagPath, position}, requestParams);
    }


    deleteMenuItemV4(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        path: string,
    ): Observable<any> {
        const requestParams = MenuService.getParamsWithLocation(effectiveDate, menuKey);
        return this.http.patch(`${this.getPatchBaseUrlV4()}/remove`, {itemPath: path},
            requestParams);
    }


    deleteMenuItemCascadeV4(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        path: string,
        submenus: string[],
    ): Observable<any> {
        const requestParams = MenuService.getDateParam(effectiveDate);
        const pathArray = path.split('/');
        return this.http.patch(`${this.getPatchBaseUrlV4()}/remove/cascade`,
            {
                subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey.submenu}`],
                parentMatchPath: `/${pathArray[pathArray.length - 2]}`,
                item: pathArray[pathArray.length - 1],
            }, requestParams);
    }

    pasteMenuItemV4(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        fromPath: string,
        toPath: string
    ): Observable<any> {
        const requestParams = MenuService.getDateParam(effectiveDate);
        return this.http.patch(
            `${this.getPatchBaseUrlV4()}/copy`,
            {
                copyFrom: fromPath,
                copyTo: toPath
            },
            requestParams
        );
    }


    copyMenu(menuCopy: {
        consumerFromTag: string;
        locationFromTag: string;
        consumerToName: string;
        consumerToTag: string;
        locationToTag: string;
        consumerTypeToTag: string;
        locked: boolean;
    }): Observable<any> {
        return this.http.post(`${this.apiUrl}/emm/menu-copy`, menuCopy);
    }


    getDeleteCascadePath(parentPath: string, childTag: string, featureScope: string) {
        if (featureScope === 'Global') {
            return `${parentPath}/${this.getNodeTag(childTag, featureScope)}`;
        } else {
            return `${parentPath}..items${this.getNodeTag(childTag, featureScope)}`;
        }
    }



    getParentCascadePathForAll(parentTag: string, featureScope: string) {
        if (featureScope === 'Global' || featureScope === 'Viewer') {
            return `${this.getNodeTag(parentTag, featureScope)}`;
        }  else {
            return `$..${this.getNodeTag(parentTag, featureScope)}`;
        }
    }



    getParentCascadePathForSubmenu(subMenuPath: string, parentTag: string, featureScope: string) {
        return `${subMenuPath}..${this.getNodeTag(parentTag, featureScope)}`;
    }



    getNodeTag(tag: string, featureScope: string) {
        if (featureScope === 'Global' || featureScope === 'Viewer') {
            return tag;
        }  else {
            return `[?(@.tag=='${tag}')]`; // not sure if this is necessary for Consumer Menu Assignment, so I left it for now
        }
    }

    countDescendantsForPasteCascade(
        parentMatchPath: string,
        sourceItemPath: string,
        submenus: string[],
    ) {
        const body = {
            subMenus: submenus,
            parentMatchPath,
            sourceItemPath
        };
        return this.http.post(`${this.getPatchBaseUrlV4()}/paste/cascade/count`, body);
    }

    countMatchesAdd(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        path: string,
        submenus: string[],
    ): Observable<any> {
        const requestParams = MenuService.getDateParam(effectiveDate);
        const pathArray = path.split('/');
        const body = {
            subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey['menuKey'].submenu}`],
            parentMatchPath: `/${pathArray[pathArray.length - 1]}`,
            item: pathArray[pathArray.length - 1],
        };
        return this.http.post(`${this.getPatchBaseUrlV4()}/add/cascade/count`, body, requestParams);
    }
    countMatchesDelete(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        path: string,
        submenus: string[],
    ): Observable<any> {
        const requestParams = MenuService.getDateParam(effectiveDate);
        const pathArray = path.split('/');
        const body = {
            subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey['menuKey'].submenu}`],
            parentMatchPath: pathArray.length === 2 ? `/` : `/${pathArray[pathArray.length - 2]}`,
            item: pathArray[pathArray.length - 1],
        };
        return this.http.post(`${this.getPatchBaseUrlV4()}/remove/cascade/count`, body, requestParams);
    }

    countMatchesForUpdateCascade(
        featureScope: string,
        effectiveDate: string,
        menuKey: MenuKeyContext,
        path: string | object,
        submenus: any[],
    ): Observable<any> {
        const requestParams = MenuService.getDateParam(effectiveDate);
        let body;

        if(path['ancestorMatchPath']) {
            body = {
                subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey.submenu}`],
                itemMatchPath: path['itemMatchPath'],
                ancestorMatchPath: path['ancestorMatchPath'],
            };
        } else {
            body = {
                subMenus: (submenus && submenus.length > 0) ? submenus : [`${menuKey.submenu}`],
                itemMatchPath: path['itemMatchPath'],
            };
        }

        return this.http.post(`${this.getPatchBaseUrlV4()}/update/cascade/count`, body, requestParams);
    }


    deleteCache(): Observable<any> {
        return this.http.delete(`${this.apiUrl}/emm/cache`);
    }


    copyProdData(): Observable<any> {
        if (environment.backgroundColor !== 'white') {
            return this.http.post(`${environment.lambdaGatewayUrl}/invoke-backup-restore`, {
                dataSourceEnv: 'prod',
            });
        } else {
            return of(null);
        }
    }


    deleteCacheByName(name: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/emm/cache/${name}`);
    }


    getSubmenuCacheEnabled(): Observable<boolean> {
        return this.http.get<boolean>(`${this.apiUrl}/submenu/v4/cache/enabled`);
    }


    getMenuItemsV4(effectiveDate: string, menuKey: MenuKey, tagPath: string = ''): Observable<ItemSummary[]> {
        tagPath = tagPath ? tagPath : '/';
        const requestParams = MenuService.getParamsWithLocation(effectiveDate, menuKey);
        return this.http.get<ItemSummary[]>(
            `${this.getMenuBaseUrlV4(menuKey)}/item/children?path=${encodeURIComponent(tagPath)}`,
            requestParams);
    }


    getSubmenuItemsV4(featureScope: string, effectiveDate: string, menuKey: MenuKeyContext, tagPath: string = ''):
        Observable<ItemSummary[]> | null {
        const submenuBaseUrl = this.getSubmenuBaseUrlV4();
        const requestParams = MenuService.getParamsWithLocation(effectiveDate, menuKey, featureScope);
        if (tagPath.length > 0) {
            return this.http.get<ItemSummary[]>(
                `${submenuBaseUrl}/item/children?path=${tagPath}`,
                requestParams);
        } else if (menuKey?.submenu){
            return this.http.get<ItemSummary[]>(
                `${submenuBaseUrl}/item/children?root=/${encodeURIComponent(menuKey.submenu)}`,
                requestParams);
        } else {
            return of([]);
        }
    }


    getMenuItemV4(effectiveDate: string, menuKey: MenuKey, path: string, language: string): Observable<ItemDetail> {
        const requestParams = MenuService.getAllParams(effectiveDate, menuKey, language);
        return this.http.get<ItemDetail>(
            `${this.getMenuBaseUrlV4(menuKey)}/item?path=${encodeURIComponent(path)}`,
            requestParams
        );
    }


    getSubmenuItemV4(featureScope: string, effectiveDate: string, menuKey: MenuKeyContext, path: string, language: string):
        Observable<ItemDetail> {
        const submenuBaseUrl = this.getSubmenuBaseUrlV4();
        const requestParams = MenuService.getAllParams(effectiveDate, menuKey, language);
        return this.http.get<ItemDetail>(
            `${submenuBaseUrl}/item?path=${encodeURIComponent(path)}`,
            requestParams
        );
    }


    getFullSubmenu(path: string) {
        const submenuBaseUrl = this.getGlobalSubmenuBaseUrlV4();
        return this.http.get<Submenu>(`${submenuBaseUrl}/${encodeURIComponent(path)}`);
    }


    // Retrieve list of submenus by feature scope
    getSubmenusV4(featureScope: string, menuKey: MenuKeyContext): Observable<Submenu[]> {
        switch (featureScope) {
            case FeatureScope.Client:
                const requestParams = {params: {consumer: menuKey.consumer}};
                return this.http.get<Submenu[]>(`${this.getSubmenuBaseUrlV4()}`, requestParams);
            default:
                return this.http.get<Submenu[]>(`${this.getSubmenuBaseUrlV4()}`);
        }
    }



    saveConsumerMenuAssignments(
        assignments: any[],
        menuKey: MenuKeyContext
    ): Observable<any> {
        return this.http.put<ItemDetail>(`${this.apiUrl}/submenu/v4/assignment`, assignments, {params: {
            consumer: menuKey.consumer }});
    }


    renameSubmenu(submenuId: string, newName: string, effectiveDate?: string): Observable<any> {
        const params = effectiveDate ? {newName, effectiveDate} : {newName};
        return this.http.put(`${this.apiUrl}/submenu/v4/rename/${submenuId}`, {}, {params});
    }


    // Retrieve full menu for viewer feature scope
    private getMenuBaseUrlV4({ consumer, location }: MenuKey): string {
        return `${this.apiUrl}/menu/v4/${consumer}/${location}`;
    }


    // Retrieve submenu base url based on feature scope
    private getSubmenuBaseUrlV4(): string {
        return this.getGlobalSubmenuBaseUrlV4();
    }


    // Retrieve submenu for global feature scope
    private getGlobalSubmenuBaseUrlV4(): string {
        return `${this.apiUrl}/submenu/v4`;
    }


    private getPatchBaseUrlV4(): string {
        return `${this.getGlobalSubmenuBaseUrlV4()}/item`;
    }
}
